import apiClient from '@/services/api';

export interface CountryStateData {
    code: string;
    name: string;
    phoneCode: string;
    flag: string;
    states: string[];
}

export interface ApplicationThemeData {
    id: string;
    theme_name: string;
    impact_description: string;
}

export interface OpenCohortData {
    id: string;
    cohort_name: string;
    generated_cohort_title: string;
    start_date: string;
    registration_start: string;
    registration_end: string;
}

export interface SendBrochurePayload {
    name: string;
    email: string;
}

export interface CreateApplicationPayload {
    applicant_type: string;
    student_name: string;
    parent_name: string;
    email: string;
    contact_number: string;
    city: string;
    state: string;
    country: string;
    school_name: string;
    school_type: string;
    grade: string;
    programming_experience: number;
    accomplishments: string;
    theme_ids: string[];
    applied_cohort_id: string;
    discovery_source: string;
    referral_code: string;
    referral_name?: string;
}

export interface ApiResponse<T> {
    status: number;
    error: boolean;
    message: string;
    data: T;
}

const applicationsService = {
    getCountriesStates: async (): Promise<ApiResponse<CountryStateData[]>> => {
        const res = await apiClient.get<ApiResponse<CountryStateData[]>>('/applications/metadata/countries');
        return res.data;
    },

    getThemes: async (): Promise<ApiResponse<ApplicationThemeData[]>> => {
        const res = await apiClient.get<ApiResponse<ApplicationThemeData[]>>('/applications/metadata/themes');
        return res.data;
    },

    getOpenCohorts: async (): Promise<ApiResponse<OpenCohortData[]>> => {
        const res = await apiClient.get<ApiResponse<OpenCohortData[]>>('/applications/metadata/open-cohorts');
        return res.data;
    },

    validateEmail: async (email: string, cohortId: string): Promise<ApiResponse<{ isAvailable: boolean }>> => {
        const res = await apiClient.post<ApiResponse<{ isAvailable: boolean }>>('/applications/validate-email', {
            email,
            cohort_id: cohortId,
        });
        return res.data;
    },

    sendBrochure: async (
        payload: SendBrochurePayload,
    ): Promise<ApiResponse<{ email: string; queued: boolean }>> => {
        const res = await apiClient.post<ApiResponse<{ email: string; queued: boolean }>>(
            '/applications/send-brochure',
            payload,
        );
        return res.data;
    },

    submitApplication: async (payload: CreateApplicationPayload): Promise<ApiResponse<any>> => {
        const res = await apiClient.post<ApiResponse<any>>('/applications', payload);
        return res.data;
    },

    adminList: async (
        page: number,
        limit: number,
        search?: string,
        cohortId?: string,
        assignmentStatus?: string,
        paymentStatus?: string,
        mentorId?: string,
    ): Promise<ApiResponse<{ data: any[]; total: number }>> => {
        const params: any = { page, limit };
        if (search) params.search = search;
        if (cohortId) params.cohort_id = cohortId;
        if (assignmentStatus) params.assignment_status = assignmentStatus;
        if (paymentStatus) params.payment_status = paymentStatus;
        if (mentorId) params.mentor_id = mentorId;
        const res = await apiClient.get<ApiResponse<{ data: any[]; total: number }>>('/applications/admin/list', { params });
        return res.data;
    },

    adminListEligible: async (cohortId: string): Promise<ApiResponse<any[]>> => {
        const res = await apiClient.get<ApiResponse<any[]>>('/applications/admin/eligible', {
            params: { cohort_id: cohortId }
        });
        return res.data;
    },

    adminAssign: async (cohortId: string, applicationIds: string[]): Promise<ApiResponse<any>> => {
        const res = await apiClient.post<ApiResponse<any>>('/applications/admin/assign', {
            cohortId,
            applicationIds,
        });
        return res.data;
    },

    deleteApplication: async (id: string): Promise<ApiResponse<null>> => {
        const res = await apiClient.delete<ApiResponse<null>>(`/applications/admin/${id}`);
        return res.data;
    },

    assignMentor: async (id: string, mentorId?: string | null): Promise<ApiResponse<any>> => {
        const res = await apiClient.post<ApiResponse<any>>(`/applications/admin/${id}/assign-mentor`, { mentorId });
        return res.data;
    },

    deallocateMentor: async (id: string): Promise<ApiResponse<any>> => {
        const res = await apiClient.post<ApiResponse<any>>(`/applications/admin/${id}/deallocate-mentor`);
        return res.data;
    },

    adminListEligibleForMentor: async (): Promise<ApiResponse<any[]>> => {
        const res = await apiClient.get<ApiResponse<any[]>>('/applications/admin/eligible-for-mentor');
        return res.data;
    },

    assignMentorBulk: async (mentorId: string, applicationIds: string[]): Promise<ApiResponse<any>> => {
        const res = await apiClient.post<ApiResponse<any>>('/applications/admin/assign-mentor-bulk', {
            mentorId,
            applicationIds,
        });
        return res.data;
    },

    resendPaymentLink: async (id: string): Promise<ApiResponse<any>> => {
        const res = await apiClient.post<ApiResponse<any>>(`/applications/admin/${id}/resend-payment-link`);
        return res.data;
    },

    startExport: async (filters: {
        search?: string;
        cohortId?: string;
        assignmentStatus?: string;
        paymentStatus?: string;
        mentorId?: string;
    }): Promise<ApiResponse<{ jobId: string; status: string }>> => {
        const res = await apiClient.post<ApiResponse<{ jobId: string; status: string }>>(
            '/applications/admin/export',
            filters,
        );
        return res.data;
    },

    getExportStatus: async (
        jobId: string,
    ): Promise<ApiResponse<{ jobId: string; state: string; progress: number; result: { downloadUrl: string } | null }>> => {
        const res = await apiClient.get<ApiResponse<{ jobId: string; state: string; progress: number; result: { downloadUrl: string } | null }>>(
            `/applications/admin/export/status/${jobId}`,
        );
        return res.data;
    },
};

export default applicationsService;
